// Insights chart wrappers — Recharts themed to the Westfert palette, plus two
// bespoke grids (Heatmap, CohortGrid) that Recharts doesn't cover. All read the
// shared PALETTE + formatters so they match the rest of the app.

import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, Cell,
} from "recharts";
import { PALETTE, fmtRm, fmtN } from "../lib/format.js";
import { useInView } from "./Charts.jsx";
import { usePrinting } from "./PrintContext.jsx";

const AXIS = { fontSize: 11, fill: "var(--muted)" };
const GRID = "rgba(120,140,170,0.15)";
// Fixed dark tooltip — readable over both light and dark charts. Recharts
// colours the label + item rows separately, so set all three.
const tipStyle = {
  background: "#13241A", border: "1px solid rgba(255,255,255,.12)",
  borderRadius: 10, fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,.3)",
};
const tipLabel = { color: "#fff", fontWeight: 700 };
const tipItem = { color: "#CDE39E" };

const rand = (v) => `R${(Math.abs(v) / 1e6).toFixed(1)}m`;

export function ChartFrame({ title, sub, children, height = 300 }) {
  const [ref, inView] = useInView();   // mount the chart only once visible → its animation plays on scroll-in
  const printing = usePrinting();
  // Shrink for print via React state (not @media print CSS) so ResponsiveContainer's
  // ResizeObserver re-measures to the smaller box BEFORE window.print — otherwise the
  // chart keeps its on-screen height and spills into the card below in the PDF.
  const h = printing ? 220 : height;
  return (
    <div className="card pad" ref={ref} style={{ padding: 16 }}>
      {title && <h3 style={{ margin: "0 0 2px", font: "600 15px var(--font-head)", color: "var(--ink)" }}>{title}</h3>}
      {sub && <div className="hint" style={{ marginBottom: 10 }}>{sub}</div>}
      <div className="chart-body" style={{ width: "100%", height: h }}>
        {inView && <ResponsiveContainer>{children}</ResponsiveContainer>}
      </div>
    </div>
  );
}

/** Multi-line time series. lines = [{key,name,color?}]. */
export function TrendLine({ data, xKey = "month", lines, money = true }) {
  const printing = usePrinting();
  return (
    <LineChart data={data} margin={{ top: 6, right: 12, bottom: 0, left: 6 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={24} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} width={54}
        tickFormatter={money ? rand : fmtN} />
      <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} itemStyle={tipItem} formatter={(v) => (money ? fmtRm(v) : fmtN(v))} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      {lines.map((l, i) => (
        <Line key={l.key} type="monotone" dataKey={l.key} name={l.name ?? l.key} isAnimationActive={!printing}
          stroke={l.color ?? PALETTE[i % PALETTE.length]} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} />
      ))}
    </LineChart>
  );
}

/** Stacked area. keys = [{key,name,color?}]. percent → 100% stacked. */
export function StackedArea({ data, xKey = "month", keys, percent = false, money = true }) {
  const printing = usePrinting();
  return (
    <AreaChart data={data} stackOffset={percent ? "expand" : undefined} margin={{ top: 6, right: 12, bottom: 0, left: 6 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={24} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} width={percent ? 38 : 54}
        tickFormatter={percent ? (v) => `${Math.round(v * 100)}%` : (money ? rand : fmtN)} />
      <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} itemStyle={tipItem} formatter={(v) => (money ? fmtRm(v) : fmtN(v))} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      {keys.map((k, i) => (
        <Area key={k.key} type="monotone" dataKey={k.key} name={k.name ?? k.key} stackId="1" isAnimationActive={!printing}
          stroke={k.color ?? PALETTE[i % PALETTE.length]} fill={k.color ?? PALETTE[i % PALETTE.length]} fillOpacity={0.78} />
      ))}
    </AreaChart>
  );
}

/** Simple categorical bars (backlog aging, collections mix). */
export function ThemedBars({ data, xKey, valueKey, money = true, colorByIndex = true, color }) {
  const printing = usePrinting();
  return (
    <BarChart data={data} margin={{ top: 6, right: 12, bottom: 0, left: 6 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} width={54} tickFormatter={money ? rand : fmtN} />
      <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} itemStyle={tipItem} formatter={(v) => (money ? fmtRm(v) : fmtN(v))} cursor={{ fill: "rgba(0,114,206,0.06)" }} />
      <Bar dataKey={valueKey} radius={[6, 6, 0, 0]} isAnimationActive={!printing}>
        {data.map((_, i) => <Cell key={i} fill={colorByIndex ? PALETTE[i % PALETTE.length] : (color ?? PALETTE[0])} />)}
      </Bar>
    </BarChart>
  );
}

/** Pareto / Lorenz curve: cumulative revenue % vs cumulative customer %. */
export function Pareto({ data }) {
  const printing = usePrinting();
  return (
    <ComposedChart data={data} margin={{ top: 6, right: 12, bottom: 4, left: 6 }}>
      <CartesianGrid stroke={GRID} />
      <XAxis dataKey="cust_pct" type="number" domain={[0, 100]} tick={AXIS} tickLine={false}
        axisLine={{ stroke: GRID }} tickFormatter={(v) => `${v}%`} label={{ value: "% of customers", position: "insideBottom", offset: -2, fontSize: 11, fill: "var(--muted)" }} />
      <YAxis type="number" domain={[0, 100]} tick={AXIS} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => `${v}%`} />
      <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} itemStyle={tipItem} formatter={(v) => `${v}%`} labelFormatter={(v) => `Top ${v}% of customers`} />
      <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]} stroke="var(--muted)" strokeDasharray="4 4" />
      <Line type="monotone" dataKey="rev_pct" name="cumulative revenue %" stroke={PALETTE[0]} strokeWidth={2.6} dot={false} isAnimationActive={!printing} />
    </ComposedChart>
  );
}

/** Seasonality heatmap: rows = years, cols = months. cells = [{month,year,revenue}]. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function Heatmap({ cells }) {
  const years = [...new Set(cells.map((c) => c.year))].sort();
  const max = Math.max(1, ...cells.map((c) => c.revenue || 0));
  const at = (y, m) => cells.find((c) => c.year === y && c.month === m)?.revenue ?? null;
  const bg = (v) => (v == null ? "transparent" : `rgba(0,114,206,${0.08 + 0.92 * (v / max)})`);
  return (
    <div className="card pad" style={{ padding: 16, overflowX: "auto" }}>
      <h3 style={{ margin: "0 0 10px", font: "600 15px var(--font-head)", color: "var(--ink)" }}>Revenue seasonality</h3>
      <div style={{ display: "grid", gridTemplateColumns: `46px repeat(12, 1fr)`, gap: 4, minWidth: 620 }}>
        <div />
        {MONTHS.map((m) => <div key={m} style={{ textAlign: "center", fontSize: 11, color: "var(--muted)" }}>{m}</div>)}
        {years.map((y) => (
          <span key={y} style={{ display: "contents" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", alignSelf: "center" }}>{y}</div>
            {MONTHS.map((_, mi) => {
              const v = at(y, mi + 1);
              return (
                <div key={mi} title={v != null ? `${MONTHS[mi]} ${y}: ${fmtRm(v)}` : ""}
                  style={{ height: 34, borderRadius: 6, background: bg(v), display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, color: v != null && v / max > 0.55 ? "#fff" : "var(--muted)" }}>
                  {v != null ? `${Math.round(v / 1e6)}` : ""}
                </div>
              );
            })}
          </span>
        ))}
      </div>
      <div className="hint" style={{ marginTop: 8 }}>Cell = revenue (Rm). Darker = higher. Reveals the planting-season rhythm.</div>
    </div>
  );
}

/** Cohort retention grid: rows = first-purchase quarter, cols = later quarters, value = % of cohort's opening revenue retained. */
const qLabel = (code) => `${Math.floor(code / 10)} Q${code % 10}`;
export function CohortGrid({ rows }) {
  const cohorts = [...new Set(rows.map((r) => r.cohort))].sort();
  const periods = [...new Set(rows.map((r) => r.period))].sort();
  const val = (c, p) => rows.find((r) => r.cohort === c && r.period === p)?.revenue ?? null;
  const pct = (c, p) => { const base = val(c, c); const v = val(c, p); return base && v != null ? (v / base) * 100 : null; };
  const bg = (r) => (r == null ? "transparent" : `rgba(31,168,106,${0.12 + 0.88 * Math.min(1, r / 100)})`);
  return (
    <div className="card pad" style={{ padding: 16, overflowX: "auto" }}>
      <h3 style={{ margin: "0 0 4px", font: "600 15px var(--font-head)", color: "var(--ink)" }}>Customer cohort retention</h3>
      <div className="hint" style={{ marginBottom: 10 }}>Each row = customers acquired that quarter; cells = % of their opening-quarter revenue still spent in later quarters.</div>
      <div style={{ display: "grid", gridTemplateColumns: `64px repeat(${periods.length}, minmax(48px,1fr))`, gap: 4, minWidth: 560 }}>
        <div />
        {periods.map((p) => <div key={p} style={{ textAlign: "center", fontSize: 10, color: "var(--muted)" }}>{qLabel(p)}</div>)}
        {cohorts.map((c) => (
          <span key={c} style={{ display: "contents" }}>
            <div style={{ fontSize: 11, color: "var(--ink)", fontWeight: 600, alignSelf: "center" }}>{qLabel(c)}</div>
            {periods.map((p) => {
              const r = p < c ? null : pct(c, p);
              return (
                <div key={p} title={r != null ? `${qLabel(c)} → ${qLabel(p)}: ${r.toFixed(0)}%` : ""}
                  style={{ height: 30, borderRadius: 6, background: bg(r), display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, color: r != null && r > 60 ? "#fff" : "var(--muted)" }}>
                  {r != null ? `${Math.round(r)}` : ""}
                </div>
              );
            })}
          </span>
        ))}
      </div>
    </div>
  );
}
